/**
 * Pure helpers for a turn's status line, shared by the chat's status line and
 * the drawer's Activity board so the two always say the same thing.
 */

/**
 * "8.2s" / "24s" / "1m 4s". Returns "" for a missing or negative duration.
 * @param {number|null|undefined} ms
 * @returns {string}
 */
export function formatDuration(ms) {
  if (!ms || ms < 0) return "";
  const seconds = ms / 1000;
  if (seconds < 10) return `${seconds.toFixed(1)}s`;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${rest}s`;
}

/**
 * "0:42" / "1:12": a running clock, in whole seconds so it ticks steadily.
 * @param {number} ms
 * @returns {string}
 */
export function formatElapsed(ms) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const rest = String(seconds % 60).padStart(2, "0");
  return `${Math.floor(seconds / 60)}:${rest}`;
}

/**
 * Oliver is working rather than writing: the line moves with each step until the answer starts.
 * @param {Object|null} activity
 * @returns {boolean}
 */
export function isWorking(activity) {
  return Boolean(activity) && !activity.done && activity.phase !== "writing";
}

/**
 * Oliver finished its work and was not stopped: its steps close with "Done".
 * @param {Object|null} activity
 * @returns {boolean}
 */
export function finishedWork(activity) {
  return (
    !isWorking(activity) &&
    activity?.outcome !== "failed" &&
    activity?.outcome !== "cancelled"
  );
}

/**
 * What the working line says: the newest step's line, the model's own words for it.
 * @param {Object|null} activity
 * @returns {string}
 */
export function workingLine(activity) {
  if (activity?.phase === "resuming") return "Still working";
  const steps = activity?.steps || [];
  return steps[steps.length - 1]?.label || "Thinking";
}

/**
 * What the line says once the answer has started.
 * @param {Object|null} activity
 * @returns {string}
 */
export function workedLine(activity) {
  const duration = formatDuration(activity?.durationMs);
  if (activity?.outcome === "failed") return "Couldn't finish";
  if (activity?.outcome === "cancelled") {
    return duration ? `Stopped after ${duration}` : "Stopped";
  }
  return duration ? `Worked for ${duration}` : "Steps taken";
}
